import { useEffect, useState, useCallback, createContext, useContext, ReactNode } from 'react';
import React from 'react';
import { addLocale, useLocale as ttagUseLocale } from 'ttag';

// Create a context for locale management
interface LocaleContextType {
  locale: string;
  setLocale: (locale: string) => void;
}

const LocaleContext = createContext<LocaleContextType | null>(null);

// Global state for locale management
let currentLocale = 'en';
let localeListeners: Set<() => void> = new Set();

// Global function to update locale and notify all listeners
const updateGlobalLocale = (newLocale: string) => {
  if (currentLocale === newLocale) {
    return;
  }

  try {
    // Load translation file
    const translationsObj = require(`../locale/${newLocale}.po.json`);
    if (!translationsObj) {
      console.warn(`Translation file not found for locale: ${newLocale}`);
      return;
    }

    // Update ttag locale
    addLocale(newLocale, translationsObj);
    ttagUseLocale(newLocale);

    // Update global state
    currentLocale = newLocale;

    // Update localStorage
    window.localStorage.setItem('locale', newLocale);

    // Notify all listeners to trigger re-renders
    localeListeners.forEach((listener) => listener());

  } catch (error) {
    console.error(`Failed to load locale ${newLocale}:`, error);
  }
};

/**
 * Custom hook for locale management that ensures components re-render when locale changes
 * @returns Object with current locale and setLocale function
 */
export const useLocale = () => {
  const context = useContext(LocaleContext);
  const [locale, setLocaleState] = useState(currentLocale);

  // Subscribe to locale changes
  useEffect(() => {
    const listener = () => {
      setLocaleState(currentLocale);
    };

    localeListeners.add(listener);

    return () => {
      localeListeners.delete(listener);
    };
  }, []);

  const setLocale = useCallback((newLocale: string) => {
    updateGlobalLocale(newLocale);
  }, []);

  return {
    locale,
    setLocale,
  };
};

/**
 * Initialize locale on app startup
 */
export const initializeLocale = () => {
  const initialLocale = getLocale();
  updateGlobalLocale(initialLocale);
};

/**
 * Get current locale without subscribing to changes
 * @returns Current locale string
 */
export const getCurrentLocale = () => currentLocale;

/**
 * Set locale without using the hook (for use in non-React contexts like stores)
 * @param newLocale - The new locale to set
 */
export const setGlobalLocale = updateGlobalLocale;

const LOCALE_COOKIE = '__locale';

export const getLocale = (): string => {
  try {
    // Try to get locale from cookies first
    const cookieValue = document.cookie
      .split(';')
      .map(cookie => cookie.trim())
      .find(cookie => cookie.startsWith(`${LOCALE_COOKIE}=`))
      ?.split('=')[1];

    if (cookieValue && cookieValue.trim()) {
      return cookieValue.trim();
    }

    // Fallback to localStorage
    const storageLocale = window.localStorage.getItem('locale');
    if (storageLocale && storageLocale.trim()) {
      return storageLocale.trim();
    }

    // Default fallback
    return 'en';
  } catch (error) {
    console.warn('Error getting locale, falling back to default:', error);
    return 'en';
  }
};

/**
 * Locale Provider component that provides locale context to all child components
 * This ensures that all components using translations will re-render when locale changes
 */
export const LocaleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [locale, setLocale] = useState(currentLocale);

  useEffect(() => {
    const listener = () => {
      setLocale(currentLocale);
    };

    localeListeners.add(listener);

    return () => {
      localeListeners.delete(listener);
    };
  }, []);

  const value: LocaleContextType = {
    locale,
    setLocale: updateGlobalLocale,
  };

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};
